<script setup>

import Button from "@/Components/Button.vue";
import Card from "@/Components/Card.vue";
import {router, usePage} from "@inertiajs/vue3";
import InputError from "@/Components/InputError.vue";
import {computed, ref} from "vue";
import VueMultiselect from 'vue-multiselect/dist/vue-multiselect.ssr.js'
import PaymentMethod from "@/Pages/Offer/Components/PaymentMethod.vue";
import Checkbox from "@/Components/Checkbox.vue";
import {priceNumToStr, priceStrToNum} from "@/helper.js";
import floor from "lodash-es/floor";
import ceil from "lodash-es/ceil";

// Vite 8's Rolldown dep pre-bundler doesn't unwrap this CJS module's `__esModule`/`default`
// interop, so a plain default import resolves to the whole exports object (no render function)
// instead of the component - see docs/plans/dependency-upgrade-2026-07.md.
const Multiselect = VueMultiselect.default ?? VueMultiselect

const props = defineProps({
    price: Number,
    // Offer/Show.vue mounts two OfferPay instances (desktop-only and mobile-only, toggled via
    // the hidden/flex classes passed as $attrs). Each has its own independent local state, so
    // the fixed mobile CTA bar must come from the mobile instance only - one showing it from
    // the desktop instance too would render a second bar that's stuck reflecting a payment
    // method never selected in the form the buyer can actually see.
    //
    // This is also why the two instances can't be collapsed into one responsive instance (an
    // accessibility audit flagged the CSS-hidden instance's card/order button as a low-priority
    // DOM-duplication cleanup): on desktop the card sits inside the left column,
    // before the seller chat; on mobile it sits standalone, after it - see Offer/Show.vue's
    // flex-col (mobile) / lg:flex-row (desktop) layout. That's a real position swap across the
    // chat, not just a size toggle, so a single instance would need order-* flex tricks across
    // an axis change, not a plain responsive class. The hidden instance is already excluded from
    // offsetParent (Tailwind's `hidden`), so screen readers already skip it correctly - left as
    // two instances rather than risking this layout for a cosmetic-only cleanup.
    mobileCta: {
        type: Boolean,
        default: false
    }
})

const page = usePage()
const paymentMethods = page.props.payment_methods
// Первый метод выбран заранее - так же, как на экране покупки робуксов
// (Pages/Roblox/Payment.vue), самом нагруженном платёжном экране приложения. Здесь этого не было, и
// покупатель обязан был открыть селектор, прежде чем кнопка вообще начинала что-то делать; на
// мобильном ради этого пришлось завести отдельную ветку в mobileCtaClick() ниже.
//
// Порядок списка задаёт `payment.enabled_drivers`, то есть первым идёт СБП. Это важно: замер
// 13.08.2026 по 90 дням показал, что из 48 заказов, потребовавших оплаты, больше одной попытки
// сделали трое - неудачный выбор метода означает потерянный заказ, а не переключение на другой
// способ. При этом СБП оплачивается в 47% случаев, карта - в 18%
// (docs/plans/marketplace-growth-2026-08-13.md, C3).
const selectPaymentMethods = ref(paymentMethods?.[0])
const useWallet = ref(page.props.wallet_balance > 0)

const deductWalletAmount = ref(0)

const getDeductWalletBalanceString = () => priceNumToStr(deductWalletAmount.value * page.props.currency.rate)

const deductWalletAmountString = ref(getDeductWalletBalanceString())
const deductWalletAmountUpdate = (event) => {
    deductWalletAmount.value = floor(
        priceStrToNum(event.target.value) / page.props.currency.rate * 10 ** page.props.currency.precision, 0)
}

const pay = () => {
    router.post(route('orders.store', {offer: page.props.offer.id}), {
        wallet_amount: deductWalletAmount.value,
        method: selectPaymentMethods.value?.value
    })
}

const walletMaxAmount = computed(() => {
    return page.props.wallet_balance > props.price ? props.price : page.props.wallet_balance
})
const selectMaxAmount = () => {
    deductWalletAmount.value = walletMaxAmount.value
    deductWalletAmountString.value =
        priceNumToStr(ceil(deductWalletAmount.value * page.props.currency.rate) / 10 ** page.props.currency.precision)
}

const minPaymentAmount = computed(() => Math.floor((selectPaymentMethods.value?.min_amount / 100) * selectPaymentMethods.value.currency_rate));

const canPay = computed(() => !!selectPaymentMethods.value || deductWalletAmount.value === props.price);

// The mobile CTA bar has no room for the payment-method selector itself (see the note on the
// bar below), so a plain :disabled button there just looked broken - nothing on screen explained
// why tapping it did nothing. Instead of disabling, a tap with no method chosen yet scrolls the
// selector into view and opens it, so the bar is always actionable and self-explanatory. Since the
// first method is now preselected above, this is a fallback for the one case that still has no
// method - `payment_methods` arriving empty - rather than the normal path.
const paymentMethodSelect = ref(null)
const mobileCtaClick = () => {
    if (canPay.value) {
        pay()
        return
    }
    paymentMethodSelect.value?.$el.scrollIntoView({behavior: 'smooth', block: 'center'})
    paymentMethodSelect.value?.activate()
}

</script>

<template>
    <!-- Two root nodes below (the card + the sticky mobile bar) mean Vue no longer auto-inherits
         the caller's class (e.g. Offer/Show.vue's "hidden lg:flex"/"flex lg:hidden" desktop-vs-mobile
         toggle) onto a single root, so it's bound explicitly here instead - the sticky bar keeps
         its own always-on lg:hidden and must not receive it. -->
    <card v-bind="$attrs" class="flex px-3 py-5 flex-col gap-2.5 w-full">
        <div class="px-2.5 flex gap-1.5 items-end">
            <span class="text-sm dark:text-slate-200 font-medium uppercase leading-full">{{ $t('Price') }}</span>
            <div class="flex items-end gap-1 leading-full">
                <span class="text-xl dark:text-slate-200 font-medium leading-full ">{{ $page.props.offer.price_currency_formatted }}</span>
            </div>
        </div>
        <div class="px-2.5 text-xs text-slate-500 dark:text-slate-400 -mt-1.5">
            <template v-if="selectPaymentMethods">
                {{ $t('Total to pay: :amount', {amount: selectPaymentMethods.currency_amount_formatted}) }}
            </template>
            <template v-else>
                {{ $t('Price excludes the payment provider fee') }}
            </template>
        </div>
        <div class="px-2.5 sm:px-0 flex gap-1.5 items-end my-2.5">
            <div class="px-2.5 dark:bg-deep-green py-2 bg-green-50 w-full flex items-stretch content-center rounded-lg">
                <div class="w-[24px]">
                    <ion-icon class="text-2xl fill-green-600" name="shield-checkmark" style="width: 24px; height: 24px"></ion-icon>
                </div>

                <div class="ml-2.5">
                    <p class="text-sm font-medium dark:text-slate-200 font-sans leading-[.875rem]">
                        {{ $t('Your money is safe') }}
                    </p>
                    <p class="text-sm font-sans dark:text-slate-200 leading-[.875rem]">
                        {{ $t('The seller will not receive payment until you confirm the fulfillment of the order.') }}
                    </p>
                </div>
            </div>
        </div>
        <Multiselect
            ref="paymentMethodSelect"
            v-model="selectPaymentMethods" :close-on-select="true" :options="paymentMethods"
            :placeholder="$t('Select payment method')" :searchable="false"
            :show-labels="false" track-by="value"
            class="border-gray-300 focus:border-blue-600 focus:ring-blue-600 rounded-md shadow-xs"
        >
            <!--suppress VueUnrecognizedSlot -->
            <template #option="props">
                <PaymentMethod :props="props"/>
            </template>
            <!--suppress VueUnrecognizedSlot -->
            <template #placeholder>
                <span class="font-sans">
                    {{ $t('Select payment method') }}
                </span>
            </template>
            <!--suppress VueUnrecognizedSlot -->
            <template #singleLabel="props">
                <PaymentMethod :props="props"/>
            </template>
            <!--suppress VueUnrecognizedSlot -->
            <template #noResult>
                <span class="font-sans">{{ $t('No elements found. Consider changing the search query.') }}</span>
            </template>
            <!--suppress VueUnrecognizedSlot -->
            <template #noOptions>
                <span class="font-sans">{{ $t('List is empty.') }}</span>
            </template>
        </Multiselect>
        <div v-if="selectPaymentMethods?.min_amount > page.props.offer.price">
            <div class="px-2.5 py-2 w-full flex items-stretch content-center rounded-lg">
                <div class="w-[16px]">
                    <ion-icon class="text-base fill-green-600" name="alert-circle" style="width: 16px; height: 16px"></ion-icon>
                </div>
                <div class="ml-1.5">
                    <p class="text-sm font-sans text-slate-500 dark:text-slate-400 leading-[.875rem]">
                        {{$t('The minimum amount of payment by this method is :sum. The difference will be credited to your wallet for future payments.', {
                            sum: `${ minPaymentAmount } ${selectPaymentMethods.currency.toUpperCase()}`
                        })}}
                    </p>
                </div>
            </div>
        </div>
        <div v-if="page.props.wallet_balance > 0" class="px-2.5 pb-3 flex gap-2 items-center">
            <Checkbox :checked="useWallet"
                      @update:checked="useWallet = !useWallet"
            >
                <template #label>
                    <span class="dark:text-slate-200">{{$t('Use funds from the wallet')}}</span>
                </template>
            </Checkbox>
        </div>
        <template v-if="useWallet">
            <div class="px-1 flex justify-between items-center">
                <div class="text-sm text-slate-500 dark:text-slate-400">{{ $t('Available balance:') }}
                    {{ page.props.wallet_balance_formatted }}
                </div>
                <button class="bg-blue-600 hover:bg-blue-700 text-white dark:text-dark-blue rounded-full h-7 px-4 uppercase text-[0.65rem]"
                        @click="selectMaxAmount()">
                    MAX
                </button>
            </div>
            <div class="relative">
                <input
                    v-model="deductWalletAmountString"
                    :class="[deductWalletAmount > walletMaxAmount ? 'border-red-300 focus:border-red-300 focus:ring-red-300'
                : 'border-gray-300 focus:border-blue-600 focus:ring-blue-600']"
                    :placeholder="$t('Enter the amount to be debited from your wallet')"
                    class="rounded-md dark:bg-deep-blue dark:text-slate-200 shadow-xs placeholder:text-gray-400 w-full pl-8"
                    type="text"
                    @input="deductWalletAmountUpdate"
                >
                <div class="absolute inset-y-0 left-0 flex items-center pl-4">
                    <span class="text-gray-400 font-normal text-xl">{{ page.props.currency.symbol }}</span>
                </div>
            </div>
            <div v-if="deductWalletAmount > walletMaxAmount" class="text-red-500 text-sm mt-1 pl-2">
                <ion-icon name="alert-circle" class="inline-block mr-1 align-middle"></ion-icon>
                <span class="text-sm align-middle">{{ $t('The amount exceeds the wallet balance') }}</span>
            </div>
        </template>

        <InputError v-if="page.props.errors?.error" :message="page.props.errors?.error"/>
        <div class="flex pt-1 justify-center px-2.5">
            <Button :disabled="!canPay"
                    :text="$t('order')"
                    class="text-sm w-full"
                    type="primary"
                    @click="pay()"
            ></Button>
        </div>
    </card>

    <!-- Bottom CTA so the pay button doesn't require scrolling past the description and seller
         chat to reach - see the desktop OfferPay above, which is already visible without
         scrolling on wider viewports. position: sticky doesn't work here: this element's own
         natural position is already at the end of a page-length flex column (after the header,
         description and chat), so it never gets the "room" a sticky element needs to visibly
         detach from normal flow - confirmed by scrolling the actual page, it just scrolls past
         like every other block. fixed (same as the cookie banner - see resources/views/vendor/cookie-consent)
         is what actually keeps it pinned to the viewport regardless of scroll position.

         There's no room here for the payment-method selector itself, so the button is never
         :disabled (a real disabled button silently swallows clicks - a buyer tapping it with no
         method chosen had no way to tell why nothing happened). Instead mobileCtaClick() scrolls
         the selector above into view and opens it when a method isn't picked yet, and the
         button's own label says "Payment method" instead of "order" in that state - the bar is
         always actionable, whichever step the buyer is on. -->
    <div v-if="mobileCta" class="lg:hidden fixed bottom-0 inset-x-0 z-20 flex items-center justify-between gap-3 bg-white dark:bg-dark-blue px-3 py-2 shadow-[0_-2px_6px_0_rgba(0,0,0,0.06)]">
        <div class="flex flex-col leading-tight overflow-hidden">
            <span class="text-[0.65rem] text-slate-500 dark:text-slate-400 uppercase">{{ $t('Price') }}</span>
            <span class="text-lg font-medium dark:text-slate-200 truncate">{{
                    selectPaymentMethods ? selectPaymentMethods.currency_amount_formatted : $page.props.offer.price_currency_formatted
                }}</span>
        </div>
        <Button :text="canPay ? $t('order') : $t('Payment method')"
                class="text-sm shrink-0"
                type="primary"
                @click="mobileCtaClick()"
        ></Button>
    </div>
</template>
